import {
  ActivityFilterKey,
  ActivityFilterModel,
  getActivityFilter,
  SortDirectionEnum,
  useActivityListFilter,
  useCurrentAccount,
  useCurrentWorkspace,
  useFindUnreadActivityInbox,
  useSetActivityListFilter
} from '@gripp/shared-logic';
import { createColumnHelper } from '@tanstack/react-table';
import {
  useCallback,
  useEffect,
  useState
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  Platform,
  StyleProp,
  StyleSheet,
  Text,
  View,
  ViewStyle
} from 'react-native';
import { useParams } from 'react-router-dom';
import { Colors } from '../../themes/colors';
import { InfiniteDataGrid } from '../infiniteDataGrid';
import { ActivitySortDropdown } from './activitySortDropdown';
import { IssueCard, IssueCardData } from './issueCard';
import { IssueCardCompact } from './issueCardCompact';

type ActivityListProps = {
  assetId?: string;
  showAssetName?: boolean;
  onIssueSelected?: (id: string) => void;
  onActivityCountChange?: (count: number) => void;
  selectedIssue?: string;
  listMaxHeight?: number;
  showScrollIndicator?: boolean;
  containerStyle?: StyleProp<ViewStyle>;
  right?: React.ComponentType;
  filterKey: ActivityFilterKey;
  showTextEmpty?: boolean;
  excludeFilter?: boolean;
  onDataLoaded?: (data: any) => void;
  customEmptyState?: React.ReactNode;
  /**
   * Which card to render inside the list.
   * - default: full IssueCard (existing behavior)
   * - assetCompact: compact card for asset preview (title+icons+date+description)
   */
  cardVariant?: 'default' | 'assetCompact';
  /**
   * Page size used by the infinite grid query.
   */
  pageSize?: number;
  /**
   * Whether to show the list top bar (results + sort).
   */
  showTopBar?: boolean;
};

const getOrderByField = (orderBy: any): string => {
  if (orderBy === 'MostRecentUpdated') {
    return 'updatedAt';
  }
  return orderBy;
};

export const ActivityListOld = (props: ActivityListProps) => {
  const { data: filterData, isLoading: isLoadingFilter } =
    useActivityListFilter(props.filterKey);
  const { account } = useCurrentAccount();

  const { mutateAsync: setActivityListFilter } =
    useSetActivityListFilter(props.filterKey);

  const { t } = useTranslation();
  const [filter, setFilter] = useState(undefined);
  const [inbox, setInbox] = useState<any[]>([]);
  const [itemsCount, setItemsCount] = useState(0);
  const { issueId } = useParams();

  const { data: inboxData, isLoading: isLoadingInbox } =
    useFindUnreadActivityInbox();

  const workspace = useCurrentWorkspace(true);

  const currentOrderBy = filterData?.orderBy || 'MostRecentUpdated';
  const orderByField = getOrderByField(currentOrderBy);

  useEffect(() => {
    if (inboxData) {
      setInbox(inboxData.items);
    }
  }, [inboxData]);

  useEffect(() => {
    if (!filterData || workspace === undefined) return;

    // reset filtering only for Recent Activity
    const aFilter = props.excludeFilter
      ? new ActivityFilterModel()
      : filterData;

    const f = getActivityFilter(aFilter, workspace?.id, props.assetId);

    setFilter(f);
  }, [filterData, workspace, props.assetId]);

  const hasUnread = useCallback((activity: any) => {
    return inbox.some(
      (i) =>
        (
          i.type === 'IssueUpdated' ||
          i.type === 'IssueMessageCreated'
        ) &&
        i.data.id === activity.id
    );
  }, [inbox]);

  const isMentionedInInbox = useCallback((activity: any) => {
    const inboxItem = inbox.find(
      (i) =>
        i.type === 'IssueMessageCreated' &&
        i.data.id === activity.id
    );
    return inboxItem?.data?.isMentioned || false;
  }, [inbox]);

  const rowStyle = (row: any) => {
    const isUnread = hasUnread(row.original);
    const isMentioned = isMentionedInInbox(row.original);

    if (props.cardVariant === 'assetCompact') {
      return styles.compactRow;
    }

    return [
      styles.row,
      (isUnread && isMentioned) ? styles.unread : styles.read,
    ];
  };

  const columnHelper = createColumnHelper<any>();

  const columns: any[] = [
    columnHelper.accessor('id', {
      cell: ({ row }) => {
        const activity = row.original as IssueCardData;
        return (
          activity && (
            props.cardVariant === 'assetCompact' ? (
              <IssueCardCompact
                account={account}
                activity={activity}
                showAssetName={props.showAssetName}
                isUnread={hasUnread(activity)}
                isMentionedInInbox={isMentionedInInbox(activity)}
                filter={filterData}
                isCropCircleRedesignEnabled={false}
              />
            ) : (
            <IssueCard
              account={account}
              activity={activity}
              showAssetName={props.showAssetName}
              isUnread={hasUnread(activity)}
              isMentionedInInbox={isMentionedInInbox(activity)}
              filter={filterData}
              isCropCircleRedesignEnabled={false}
            />
            )
          )
        );
      },
      meta: {
        style: styles.cell,
        customRender: true,
      },
    }),
  ];

  if (props.right) {
    const RightComponent = props.right;
    columns.push(
      columnHelper.accessor('type', {
        cell: ({ row }) => {
          const activity = row.original as IssueCardData;
          return activity && (RightComponent as any)({ activity });
        },
        meta: {
          style: styles.right,
          customRender: true,
        },
      })
    );
  }

  const onActivitySelected = (activity: any) => {
    props.onIssueSelected && props.onIssueSelected(activity.id);
  };

  const handleSortChange = async (newSort: any) => {
    if (filterData) {
      await setActivityListFilter(
        new ActivityFilterModel({
          ...filterData,
          orderBy: newSort,
        })
      );
    }
  };

  const onDataLoaded = (data: any[]) => {
    props.onDataLoaded?.(data);
  };

  if (isLoadingFilter || isLoadingInbox) {
    return null;
  }

  return !filter ? null : (
    <View
      style={[
        styles.container,
        props.cardVariant === 'assetCompact' && styles.compactContainer,
      ]}
    >
      {props.showTopBar !== false && (
        <View
          style={
            Platform.OS === 'web'
              ? styles.desktopStickyHeader
              : styles.mobileStickyHeader
          }
        >
          <Text style={styles.resultsText}>
            {itemsCount} {t('activity.topBar.results')}
          </Text>
          <ActivitySortDropdown
            selectedSort={currentOrderBy}
            onSortChange={handleSortChange}
          />
        </View>
      )}
      <InfiniteDataGrid
        columns={columns}
        query={getActivities}
        dataPrefix="issue.findIssues"
        modelName="issue"
        onRowClick={onActivitySelected}
        orderBy={{
          field: orderByField,
          order: SortDirectionEnum.Desc,
        }}
        pageSize={props.pageSize ?? 50}
        filter={filter}
        hideHeader={true}
        hideResultsLabel={true}
        refetchOnFocus={true}
        listMaxHeight={props.listMaxHeight as number}
        showScrollIndicator={props.showScrollIndicator}
        containerStyle={[
          props.containerStyle,
          props.cardVariant === 'assetCompact' && {
            backgroundColor: Colors.white,
          },
        ]}
        rowStyle={rowStyle}
        textEmpty={
          props.customEmptyState ||
          (( workspace || Platform.OS === 'web' || props.showTextEmpty) && t('activity.textEmpty'))
        }
        onItemsCountChange={(count) => {
          setItemsCount(count);
          props.onActivityCountChange?.(count);
        }}
        enableScrollRefresh={true}
        onDataLoaded={onDataLoaded}
        disableHorizontalScroll={true}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  compactContainer: {
    backgroundColor: Colors.primaryGrayBackground,
  },
  desktopStickyHeader: {
    backgroundColor: Colors.white,
    borderBottomWidth: 1,
    borderTopWidth: 1,
    borderColor: Colors.secondaryGrayHeader,
    paddingHorizontal: 16,
    paddingVertical: 12,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    height: 50,
  },
  mobileStickyHeader: {
    backgroundColor: Colors.white,
    borderBottomWidth: 1,
    borderTopWidth: 1,
    borderColor: Colors.secondaryGrayHeader,
    paddingHorizontal: 16,
    paddingVertical: 12,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    height: 50,
  },
  resultsText: {
    fontSize: 16,
    fontWeight: '500',
    color: Colors.grayText,
    letterSpacing: -0.25,
  },
  cell: {
    alignSelf: 'stretch',
    alignContent: 'stretch',
    width: '100%',
  },
  row: {
    paddingLeft: 0,
    paddingRight: 0,
    borderBottomWidth: 1,
    borderBottomColor: Colors.secondaryGrayHeader,
    height: 'auto',
    minHeight: 120,
  },
  compactRow: {
    paddingLeft: 0,
    paddingRight: 0,
    paddingTop: 2,
    paddingBottom: 2,
    borderBottomWidth: 0,
    height: 'auto',
    minHeight: 0,
    backgroundColor: Colors.primaryGrayBackground,
  },
  right: {
    maxWidth: 5,
  },
  read: {
    backgroundColor: Colors.white,
  },
  unread: {
    backgroundColor: Colors.unreadBackground,
  },
  emptyContainer: {
    flexDirection: 'column',
    rowGap: 40,
    justifyContent: 'center',
    alignItems: 'center',
    flex: 1,
    width: '100%',
  },
  textWrapper: {
    width: '100%',
  },
  emptyTapText: {
    fontWeight: '700',
    fontSize: 22,
    textAlign: 'center',
  },
});

const getActivities = `
    query FindIssues($filter: FindIssuesFilter, $page: PageRequest, $orderBy: OrderByInput) {
      issue {
        findIssues(filter: $filter, page: $page, orderBy: $orderBy) {
          count
          offset
          limit
          items {
            id
            severity
            type
            title
            subtitle
            isPinned
            asset {
              name
              coverImage {
                id
                url
                extension
                status
                type
              }
            }
            workspace {
              name
            }
            labels {
              id
              name
            }
            createdAt
            effectiveCreationDate
            createdBy {
              id
              name {
                first
                last
              }
            }
            updatedAt
            updatedBy {
              id
              name {
                first
                last
              }
            }
            nextReminderDate
            userMentions {
              id
              name {
                first
                last
              }
            }
            latestMessage {
              text
              i18n
              createdBy {
                id
                name {
                  first
                  last
                }
              }
              userMentions {
                id
                name {
                  first
                  last
                }
              }
              reactions {
                id
                createdAt
                createdBy
                reactionType
              }
              type
            }
          }
        }
      }
    }
  `;
